import './helpFaqs.scss';

import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, CardBody, Col, Collapse, ListGroup, ListGroupItem, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConditionalFragment } from 'react-conditionalfragment';
import { Background } from '../../shared/background/Background';
import { MainContainer } from '../../shared/MainContainer';
import { SearchInput } from '../../shared/searchInput/SearchInput';
import { NoResultsFound } from '../../shared/NoResultsFound';
import { CardHeaderCollapse } from '../../shared/cardHeaderCollapse/CardHeaderCollapse';
import { HtmlDisplay } from '../../../shared/htmlEditor';
import { useReplaceSearchParamsEffect, useSearchParams } from '../../../shared/useURLSearchParams';
import { LoadingIndicator } from '../../shared/LoadingIndicator';
import { HelpBanner } from '../HelpBanner';
import { useIsCampaignAdmin } from '../useIsCampaignAdmin';
import { getHelpFaqCategories, HelpFaqCategory, HelpFaqItem } from './helpFaqCategories';

/**
 * Help page of frequently asked questions for clients running campaigns, and for their delegates.
 */
export const HelpFaqs = () => {
    const { t } = useTranslation();

    // Users who run campaigns see every question, including the delegate ones. Users who only answer questionnaires only see the delegate questions.
    const isCampaignAdmin = useIsCampaignAdmin();
    const categories = useMemo(() => getHelpFaqCategories(t).filter(category => isCampaignAdmin || category.isForDelegates), [t, isCampaignAdmin]);

    // Keep the search in the query string so it survives a refresh and can be shared as a link.
    const { search: searchFromUrl } = useSearchParams();
    const [search, setSearch] = useState<string>(searchFromUrl ?? '');
    const searchParamChanges = useMemo(() => ({ search: search }), [search]);
    useReplaceSearchParamsEffect(searchParamChanges);

    // Only show the questions (and the categories that still have questions) that match the search.
    const filteredCategories = useMemo(() => {
        // Delegates have no search box, so ignore any search that came in on a link.
        const searchText = isCampaignAdmin ? search.trim().toLocaleLowerCase() : '';
        if (!searchText) {
            return categories;
        }

        return categories
            .map(category => ({
                ...category,
                items: category.items.filter(item => itemMatchesSearch(item, searchText)),
            }))
            .filter(category => category.items.length > 0);
    }, [categories, search, isCampaignAdmin]);

    // Track which answers the user has toggled. Answers start closed, except while searching when they start open so the
    // user can see why each question matched. Changing the search starts again from that default.
    const isSearching = !!isCampaignAdmin && !!search.trim();
    const [toggledItemKeys, setToggledItemKeys] = useState<Array<string>>([]);
    useEffect(() => {
        setToggledItemKeys([]);
    }, [search]);
    const toggleItem = useCallback((itemKey: string) => {
        setToggledItemKeys(previous => previous.includes(itemKey) ? previous.filter(key => key !== itemKey) : [...previous, itemKey]);
    }, [setToggledItemKeys]);
    const isItemOpen = useCallback((itemKey: string) => isSearching !== toggledItemKeys.includes(itemKey), [isSearching, toggledItemKeys]);

    const adminCategories = filteredCategories.filter(category => !category.isForDelegates);
    const delegateCategories = filteredCategories.filter(category => category.isForDelegates);

    // Wait until we know who the user is so we don't briefly show the wrong set of questions.
    if (isCampaignAdmin === undefined) {
        return (
            <Background>
                <LoadingIndicator fullWidth />
            </Background>
        );
    }

    return (
        <Background>
            <HelpBanner
                title={t('helpFaqs.heading', 'Frequently asked questions')}
                subTitle={
                    isCampaignAdmin ?
                        t('helpFaqs.subHeading', 'Answers for admins running campaigns in IMPACTS')
                        : t('helpFaqs.delegates.subHeading', 'Answers for completing your questionnaires in IMPACTS')
                }
            />
            <MainContainer className="help-faqs" fluid>
                <Row>
                    {/* Delegates only have a handful of questions, so they don't need the search or category navigation. */}
                    <ConditionalFragment showIf={isCampaignAdmin}>
                        <Col xs={12} lg={3}>
                            <div className="help-faqs-navigation">
                                <SearchInput
                                    value={search}
                                    onChange={e => setSearch(e.currentTarget.value)}
                                    placeholder={t('helpFaqs.searchPlaceholder', 'Search the FAQs')}
                                />
                                <ConditionalFragment showIf={adminCategories.length > 0}>
                                    <CategoryNavigation categories={adminCategories} />
                                </ConditionalFragment>
                                <ConditionalFragment showIf={delegateCategories.length > 0}>
                                    <div className="help-faqs-navigation-audience">{t('helpFaqs.delegatesNavigationHeading', 'Delegates')}</div>
                                    <CategoryNavigation categories={delegateCategories} />
                                </ConditionalFragment>
                            </div>
                        </Col>
                    </ConditionalFragment>

                    <Col xs={12} lg={isCampaignAdmin ? 9 : { size: 8, offset: 2 }}>
                        <p className="lead help-faqs-introduction">
                            {
                                isCampaignAdmin ?
                                    t('helpFaqs.introduction', 'How setup works, what you can change once a campaign is live, and how to read your results.')
                                    : t('helpFaqs.delegates.introduction', 'Where to find the questionnaires you\'ve been asked to complete, and the ones you\'ve already done.')
                            }
                        </p>

                        <ConditionalFragment showIf={filteredCategories.length === 0}>
                            <NoResultsFound search={search} />
                        </ConditionalFragment>

                        {filteredCategories.map(category => (
                            <section key={category.key} id={categoryElementId(category.key)} className="help-faqs-category">
                                <ConditionalFragment showIf={category.isForDelegates && isCampaignAdmin}>
                                    <Alert color="info">
                                        <FontAwesomeIcon icon="info-circle" />
                                        <> {t('helpFaqs.delegatesNote', 'These are for delegates completing a questionnaire, not campaign admins.')}</>
                                    </Alert>
                                </ConditionalFragment>
                                <div className="help-faqs-category-heading">
                                    <h2>{category.title}</h2>
                                    <span className="text-muted">
                                        {
                                            category.items.length === 1 ?
                                                t('helpFaqs.questionCountSingular', '1 question')
                                                : t('helpFaqs.questionCountPlural', '{{questionCount}} questions', { questionCount: category.items.length })
                                        }
                                    </span>
                                </div>
                                {category.items.map(item => (
                                    <FaqItem
                                        key={item.key}
                                        item={item}
                                        isOpen={isItemOpen(item.key)}
                                        toggle={() => toggleItem(item.key)}
                                    />
                                ))}
                            </section>
                        ))}

                        <Card className="help-faqs-contact">
                            <CardBody>
                                <h3>{t('helpFaqs.contact.heading', 'Can\'t find what you\'re looking for?')}</h3>
                                <p className="mb-0">
                                    {
                                        isCampaignAdmin ?
                                            t('helpFaqs.contact.text', 'Contact your account manager and they\'ll be happy to help.')
                                            : t('helpFaqs.delegates.contact.text', 'Contact the person in your organisation who asked you to complete the questionnaire.')
                                    }
                                </p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </MainContainer>
        </Background>
    );
};

/**
 * Returns the id of the element for a category, so the navigation can scroll to it.
 */
const categoryElementId = (categoryKey: string) => `help-faqs-${categoryKey}`;

/**
 * Returns true if the question or the text of the answer contains searchText, which must already be lower case.
 */
const itemMatchesSearch = (item: HelpFaqItem, searchText: string) => {
    const answerText = item.answerHtml
        .replace(/<[^>]+>/g, ' ')
        .replace(/&amp;/g, '&');

    return item.question.toLocaleLowerCase().includes(searchText)
        || answerText.toLocaleLowerCase().includes(searchText);
};

interface CategoryNavigationProps {
    categories: Array<HelpFaqCategory>,
}

/**
 * Links to each category, scrolling to the category when clicked.
 */
const CategoryNavigation = (props: CategoryNavigationProps) => {
    const { categories } = props;

    return (
        <ListGroup className="help-faqs-navigation-list">
            {categories.map(category => (
                <ListGroupItem
                    key={category.key}
                    tag="button"
                    type="button"
                    action
                    className="help-faqs-navigation-item"
                    onClick={() => document.getElementById(categoryElementId(category.key))?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                    <span>{category.navigationTitle}</span>
                    <Badge pill className="help-faqs-navigation-count">{category.items.length}</Badge>
                </ListGroupItem>
            ))}
        </ListGroup>
    );
};

interface FaqItemProps {
    item: HelpFaqItem,
    isOpen: boolean,
    toggle: () => void,
}

/**
 * A single question that expands to show its answer.
 */
const FaqItem = (props: FaqItemProps) => {
    const { item, isOpen, toggle } = props;

    return (
        <Card className="help-faqs-item">
            <CardHeaderCollapse
                isOpen={isOpen}
                toggle={toggle}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onKeyDown={(e: KeyboardEvent) => {
                    // Behave like a button for keyboard users.
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle();
                    }
                }}
                className="help-faqs-question"
            >
                {item.question}
            </CardHeaderCollapse>
            <Collapse isOpen={isOpen}>
                <CardBody className="help-faqs-answer">
                    <HtmlDisplay html={item.answerHtml} />
                </CardBody>
            </Collapse>
        </Card>
    );
};
